import { useState } from "react";
import { useLanguage, API } from "../App";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { HelpCircle, Send, Loader2, MessageCircle } from "lucide-react";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";

export const Contact = ({ onShowFAQ }) => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getRecaptchaToken = useRecaptchaToken();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  const phoneLabel = {
    fr: "Téléphone",
    es: "Teléfono",
    en: "Phone"
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken("contact");
      await axios.post(`${API}/contact`, {
        ...formData,
        language,
        recaptchaToken
      });
      toast.success(t.contact.success);
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (error) {
      console.error("Contact error:", error?.response?.data || error);
      toast.error(language === "fr" ? "Erreur lors de l'envoi" : language === "es" ? "Error al enviar" : "Error sending message");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="contact-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
          {t.contact.title}
        </h2>
        <p className="text-slate-300 text-lg">{t.contact.subtitle}</p>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
      >
        <Button
          variant="outline"
          size="lg"
          onClick={onShowFAQ}
          className="gap-2 bg-white/10 border-white/30 text-white hover:bg-white/20"
          data-testid="faq-btn"
        >
          <HelpCircle size={20} />
          {t.contact.faqBtn}
        </Button>
        <Button
          size="lg"
          className="bg-blue-600 hover:bg-blue-700 gap-2"
          onClick={() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })}
          data-testid="contact-us-btn"
        >
          <MessageCircle size={20} />
          {t.contact.contactBtn}
        </Button>
      </motion.div>

      {/* Contact Form */}
      <motion.div
        id="contact-form"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg p-6 md:p-8"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">{t.contact.name} *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t.contact.name}
                data-testid="contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">{t.contact.email} *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t.contact.email}
                data-testid="contact-email"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700">{phoneLabel[language]} *</Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+34 612 345 678"
                data-testid="contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company" className="text-slate-700">{t.contact.company}</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder={t.contact.company}
                data-testid="contact-company"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-700">{t.contact.message} *</Label>
            <Textarea
              id="message"
              required
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder={t.contact.message}
              data-testid="contact-message"
            />
          </div>

          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="contact-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="mr-2 animate-spin" />
                {language === "fr" ? "Envoi en cours..." : language === "es" ? "Enviando..." : "Sending..."}
              </>
            ) : (
              <>
                <Send size={20} className="mr-2" />
                {t.contact.send}
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default Contact;
